import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import VirtualizedDropdown, { VirtualizedDropdownOption } from '../common/VirtualizedDropdown';
import { API_ENDPOINTS, STORAGE_KEYS } from '../../config/api.config';
import { Button } from '@ohif/ui-next';
import {
  extractUniqueModalities,
  getFirstModality,
  isStudyAssigned,
  hasStudyId,
  type SelectedStudy,
} from '../../utils/studyUtils';
import { mapDicomModalityToBackend, getModalityId } from '../../utils/modalityUtils';
import {
  getAuthHeaders,
  batchAssignStudies,
  batchReassignStudies,
  convertReassignmentsToBatchFormat,
  normalizeSourceFacilityIdForApi,
  type AssignmentPayload,
  type ReassignmentPayload,
} from '../../utils/studyAssignmentUtils';
import { validateStudySelection } from '../../utils/studyValidationUtils';
import { currentUserService } from '../../services/currentUserService';
import { authService } from '../../auth/authService';
import { facilityService } from '../../services/facilityService';
import { getUserRoleFromAuthUser } from '../../utils/roleUtils';
import {
  formatPriorityDisplay,
  priorityUsesUrgentPill,
} from '../worklist/imagingWorklistTableUtils';

interface AssignRadiologistModalProps {
  checkedStudyIds: string[];
  selectedStudies?: SelectedStudy[];
  onAssign: (radiologistId: number | null, radiologistName: string) => void;
  onReassign?: (radiologistId: number | null, radiologistName: string) => void; // Callback for reassignment
  onUnassign?: () => void; // Callback for unassignment
  onClose: () => void;
  onSuccess?: () => void; // Callback after successful assignment
  isJuniorRadiologist?: boolean; // Flag to indicate if user is junior radiologist (should use reassign API)
  isSeniorRadiologist?: boolean; // Flag to indicate if user is senior radiologist (should use reassign API)
  servicesManager?: {
    services?: {
      uiNotificationService?: {
        show: (options: {
          title: string;
          message: string;
          type: 'success' | 'error' | 'warning' | 'info';
          duration?: number;
          allowDuplicates?: boolean;
        }) => void;
      };
    };
  };
}

interface Radiologist {
  id: number;
  full_name: string;
  reporting_modality_access?: string[];
  sub_role?: string;
}

function formatRadiologistSubtitle(access: string[] | undefined, emptyFallback: string): string {
  const list = (access || []).map(entry => String(entry).trim()).filter(Boolean);
  if (list.length === 0) {
    return emptyFallback;
  }
  return list.join(', ');
}

function isAsciiWhitespace(ch: string): boolean {
  return ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r' || ch === '\f' || ch === '\v';
}

/** Remove first "(Senior)" / "(Junior)" occurrence and any ASCII whitespace immediately before it. */
function stripFirstRoleParenLabel(label: string, role: 'Senior' | 'Junior'): string {
  const token = `(${role})`;
  const idx = label.toLowerCase().indexOf(token.toLowerCase());
  if (idx === -1) {
    return label;
  }
  let start = idx;
  while (start > 0 && isAsciiWhitespace(label.charAt(start - 1))) {
    start--;
  }
  return label.slice(0, start) + label.slice(idx + token.length);
}

function stripRoleParenLabels(label: string): string {
  return stripFirstRoleParenLabel(stripFirstRoleParenLabel(label, 'Senior'), 'Junior').trim();
}

/** Remove "(Senior)" / "(Junior)" at end only (optional surrounding ASCII whitespace). */
function stripSubRoleSuffixFromAssignedName(name: string): string {
  let result = name;
  for (const role of ['Senior', 'Junior'] as const) {
    const token = `(${role})`;
    let end = result.length;
    while (end > 0 && isAsciiWhitespace(result.charAt(end - 1))) {
      end--;
    }
    const withoutTrailingWs = result.slice(0, end);
    if (!withoutTrailingWs.toLowerCase().endsWith(token.toLowerCase())) {
      continue;
    }
    let start = withoutTrailingWs.length - token.length;
    while (start > 0 && isAsciiWhitespace(withoutTrailingWs.charAt(start - 1))) {
      start--;
    }
    result = withoutTrailingWs.slice(0, start);
  }
  return result.trim();
}

type RoleRequired = 'All' | 'Senior' | 'Junior';

function roleRequiredFromSubRole(subRole: string | null | undefined): RoleRequired | undefined {
  if (!subRole) {
    return undefined;
  }
  const subRoleLower = subRole.trim().toLowerCase();
  if (subRoleLower === 'junior') {
    return 'Senior';
  }
  if (subRoleLower === 'senior') {
    return 'Junior';
  }
  return undefined;
}

function roleRequiredFromReportStatuses(
  reportStatuses: string[],
  isTechnicianOrAdmin: boolean,
  hasRadiologistHierarchy: boolean
): RoleRequired | undefined {
  if (reportStatuses.length === 0) {
    return undefined;
  }
  if (isTechnicianOrAdmin && !hasRadiologistHierarchy) {
    return 'All';
  }
  const allPending = reportStatuses.every(status => status === 'pending');
  const allAwaitingReview = reportStatuses.every(status => status === 'awaiting review');
  const allDraft = reportStatuses.every(status => status === 'draft' || status === 'drafted');
  const hasPending = reportStatuses.some(status => status === 'pending');
  const hasAwaitingReview = reportStatuses.some(status => status === 'awaiting review');

  if (hasPending && hasAwaitingReview) {
    return 'All';
  }
  if (hasRadiologistHierarchy && (allPending || allDraft)) {
    return 'All';
  }
  if (isTechnicianOrAdmin && allPending) {
    return 'All';
  }
  if (allAwaitingReview) {
    return 'Senior';
  }
  if (allDraft) {
    return 'Junior';
  }
  return 'All';
}

function determineRoleRequired(
  selectedStudies: SelectedStudy[],
  userRole: string | undefined
): RoleRequired | undefined {
  const isTechnicianOrAdmin = userRole === 'Technician' || userRole === 'Admin';

  if (userRole === 'Radiologist') {
    const fromSubRole = roleRequiredFromSubRole(currentUserService.getSubRole());
    if (fromSubRole) {
      return fromSubRole;
    }
  }

  if (selectedStudies.length === 0) {
    return undefined;
  }

  const reportStatuses = selectedStudies
    .map(study => (study.reportStatus?.trim() || '').toLowerCase())
    .filter(status => status !== '');

  const hasRadiologistHierarchy = authService.getHasRadiologistHierarchy() === true;
  return roleRequiredFromReportStatuses(
    reportStatuses,
    isTechnicianOrAdmin,
    hasRadiologistHierarchy
  );
}

function formatRadiologistDropdownLabel(
  radiologist: Radiologist,
  hasRadiologistHierarchy: boolean
): string {
  let label = radiologist.full_name || '';
  if (!(hasRadiologistHierarchy && radiologist.sub_role)) {
    return label;
  }
  const subRoleLower = radiologist.sub_role.trim().toLowerCase();
  if (subRoleLower === 'senior' || subRoleLower === 'junior') {
    const subRoleDisplay = subRoleLower.charAt(0).toUpperCase() + subRoleLower.slice(1);
    label = `${label} (${subRoleDisplay})`;
  }
  return label;
}

function mapRadiologistsToDropdownOptions(
  data: Radiologist[],
  assignedRadiologistNames: string[],
  generalRadiologyLabel: string
): VirtualizedDropdownOption[] {
  const hasRadiologistHierarchy = authService.getHasRadiologistHierarchy() === true;
  return data
    .map((radiologist: Radiologist) => ({
      id: radiologist.id,
      label: formatRadiologistDropdownLabel(radiologist, hasRadiologistHierarchy),
      subtitle: formatRadiologistSubtitle(
        radiologist.reporting_modality_access,
        generalRadiologyLabel
      ),
    }))
    .filter(radiologist => {
      const radiologistNameLower = stripRoleParenLabels(radiologist.label).toLowerCase();
      return !assignedRadiologistNames.includes(radiologistNameLower);
    });
}

function getNormalizedReportStatuses(studies: SelectedStudy[]): string[] {
  return studies
    .map(study => (study.reportStatus?.trim() || '').toLowerCase())
    .filter(status => status !== '');
}

function buildAssignSuccessMessage(
  t: (key: string, options?: Record<string, unknown>) => string,
  newlyAssignedCount: number,
  reassignedCount: number,
  successCount: number,
  radiologistName: string
): string {
  if (reassignedCount > 0 && newlyAssignedCount > 0) {
    return t(
      'Successfully assigned {{newCount}} study(ies) and reassigned {{reassignCount}} study(ies) to {{radiologist}}',
      {
        newCount: newlyAssignedCount,
        reassignCount: reassignedCount,
        radiologist: radiologistName,
      }
    );
  }
  if (reassignedCount > 0) {
    return t('Successfully reassigned {{count}} study(ies) to {{radiologist}}', {
      count: reassignedCount,
      radiologist: radiologistName,
    });
  }
  return t('Successfully assigned {{count}} study(ies) to {{radiologist}}', {
    count: successCount,
    radiologist: radiologistName,
  });
}

function classifyStudyForAssignment(params: {
  study: SelectedStudy;
  studyInstanceUid: string;
  selectedRadiologist: number;
  isJuniorRadiologist: boolean;
  isSeniorRadiologist: boolean;
  hubFacility: boolean;
}):
  | { kind: 'reassignment'; payload: ReassignmentPayload }
  | { kind: 'assignment'; payload: AssignmentPayload } {
  const {
    study,
    studyInstanceUid,
    selectedRadiologist,
    isJuniorRadiologist,
    isSeniorRadiologist,
    hubFacility,
  } = params;

  if (!study.modalities) {
    throw new Error(`Study ${studyInstanceUid} not found or missing modality information`);
  }

  const isAlreadyAssigned = isStudyAssigned(study);
  const studyHasId = hasStudyId(study);
  const firstModality = getFirstModality(study.modalities);
  const backendModality = mapDicomModalityToBackend(firstModality);
  if (!backendModality) {
    throw new Error(`Unable to map modality ${firstModality} to backend format`);
  }
  const modalityId = getModalityId(backendModality);
  if (!modalityId) {
    throw new Error(`Modality ID not found for ${backendModality}`);
  }

  const isJuniorOrSeniorReassignment = isJuniorRadiologist || isSeniorRadiologist;
  if (isJuniorOrSeniorReassignment || studyHasId) {
    const assignmentStatus = isAlreadyAssigned ? 'reassigned' : 'assigned';
    if (!studyHasId && isJuniorOrSeniorReassignment) {
      throw new Error(
        `Study ${studyInstanceUid} is missing study_id. Cannot reassign without study_id.`
      );
    }
    return {
      kind: 'reassignment',
      payload: {
        study_instance_uid: studyInstanceUid,
        radiologist_id: selectedRadiologist,
        status: 'ASSIGNED',
        assignment_status: assignmentStatus,
        modality_id: modalityId,
      },
    };
  }

  const assignment: AssignmentPayload = {
    study_instance_uid: studyInstanceUid,
    modality_id: modalityId,
  };
  if (hubFacility) {
    assignment.source_facility_id = normalizeSourceFacilityIdForApi(study.sourceFacilityId);
  }
  return { kind: 'assignment', payload: assignment };
}

const AssignRadiologistModal: React.FC<AssignRadiologistModalProps> = ({
  checkedStudyIds,
  selectedStudies = [],
  onAssign,
  onReassign,
  onUnassign,
  onClose,
  onSuccess,
  isJuniorRadiologist = false,
  isSeniorRadiologist = false,
  servicesManager,
}) => {
  const { t } = useTranslation();
  const [radiologists, setRadiologists] = useState<VirtualizedDropdownOption[]>([]);
  const [selectedRadiologist, setSelectedRadiologist] = useState<number | null>(null);
  const [selectedRadiologistName, setSelectedRadiologistName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get uiNotificationService from servicesManager
  const uiNotificationService = servicesManager?.services?.uiNotificationService;

  const resolveIsHubFacility = useCallback(async (): Promise<boolean> => {
    try {
      const facilityId = authService.getFacilityId();
      if (!facilityId) {
        return false;
      }
      const response = await facilityService.getFacilityById(facilityId);
      if (!response.success || !response.data) {
        return false;
      }
      return String(response.data.facility_type || '').toUpperCase() === 'HUB';
    } catch {
      return false;
    }
  }, []);

  const studiesForDisplay = useMemo(() => {
    if (!checkedStudyIds.length) {
      return selectedStudies;
    }
    return checkedStudyIds
      .map(uid => selectedStudies.find(s => s.studyInstanceUid === uid))
      .filter((s): s is SelectedStudy => Boolean(s));
  }, [checkedStudyIds, selectedStudies]);

  // Check if any studies are already assigned
  const hasAssignedStudies = useMemo(() => {
    return selectedStudies.some(isStudyAssigned);
  }, [selectedStudies]);

  // Get unique assigned radiologist names (to filter from dropdown)
  const assignedRadiologistNames = useMemo(() => {
    const names = new Set<string>();
    selectedStudies.forEach(study => {
      if (isStudyAssigned(study)) {
        names.add(study.assignedName!.trim().toLowerCase());
      }
    });
    return Array.from(names);
  }, [selectedStudies]);

  // Extract unique modalities from selected studies
  const getSelectedModalities = useCallback((): string[] => {
    return extractUniqueModalities(selectedStudies);
  }, [selectedStudies]);

  // Fetch radiologists from API
  const fetchRadiologists = useCallback(
    async (search: string = '') => {
      setIsLoading(true);
      try {
        const facilityId = localStorage.getItem(STORAGE_KEYS.FACILITY_ID);
        const headers = getAuthHeaders();
        const selectedModalities = getSelectedModalities();
        const user = authService.getUser();
        const userRole = getUserRoleFromAuthUser(user);
        const roleRequired = determineRoleRequired(selectedStudies, userRole);

        const params: {
          page?: number;
          limit?: number;
          role?: string;
          status?: string;
          facility_id?: number;
          reporting_modality_access?: string;
          search?: string;
          name?: string;
          role_required?: string;
        } = {
          page: 1,
          limit: 100,
          role: 'Radiologist',
          status: 'Active',
        };

        if (facilityId) {
          params.facility_id = parseInt(facilityId, 10);
        }

        params.reporting_modality_access =
          selectedModalities.length > 0 ? selectedModalities.join(',') : 'CT,XR,MR';

        if (search) {
          params.search = search;
          params.name = search;
        }

        if (roleRequired) {
          params.role_required = roleRequired;
        }

        const url = API_ENDPOINTS.RADIOLOGISTS.GET(params);
        const response = await fetch(url, {
          method: 'GET',
          headers,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch radiologists: ${response.statusText}`);
        }

        const responseData = await response.json();
        const formattedRadiologists: VirtualizedDropdownOption[] =
          responseData.success && Array.isArray(responseData.data)
            ? mapRadiologistsToDropdownOptions(
                responseData.data,
                assignedRadiologistNames,
                t('General Radiology')
              )
            : [];

        setRadiologists(formattedRadiologists);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch radiologists';
        if (uiNotificationService) {
          uiNotificationService.show({
            title: t('Error'),
            message: errorMessage,
            type: 'error',
            duration: 5000,
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [getSelectedModalities, uiNotificationService, t, assignedRadiologistNames, selectedStudies]
  );

  // Initial fetch - only on mount
  useEffect(() => {
    fetchRadiologists('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search with debounce - only when searchQuery changes (not on initial empty state)
  useEffect(() => {
    // Skip if searchQuery is empty (initial state) - initial fetch already handled above
    if (!searchQuery || searchQuery.trim() === '') {
      return;
    }

    const timeoutId = setTimeout(() => {
      fetchRadiologists(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleRadiologistChange = (value: number | null, label: string) => {
    setSelectedRadiologist(value);
    setSelectedRadiologistName(label);
  };

  const showAssignValidationError = (message: string, duration = 5000) => {
    if (!uiNotificationService) {
      return;
    }
    uiNotificationService.show({
      title: t('Validation Error'),
      message,
      type: 'error',
      duration,
      allowDuplicates: true,
    });
  };

  const getAssignPrecheckError = (): string | null => {
    if (selectedRadiologist === null) {
      return t('Please select a radiologist');
    }
    const validation = validateStudySelection(selectedStudies);
    if (!validation.isValid) {
      return t(validation.errorMessage || 'Invalid study selection');
    }
    const reportStatuses = getNormalizedReportStatuses(selectedStudies);
    const hasPending = reportStatuses.some(status => status === 'pending');
    const hasAwaitingReview = reportStatuses.some(status => status === 'awaiting review');
    if (hasPending && hasAwaitingReview) {
      return t(
        'Cannot assign studies with mixed status. Please select studies with either "Pending" or "Awaiting Review" status, not both.'
      );
    }
    return null;
  };

  const partitionStudiesForAssign = async (radiologistId: number) => {
    const hubFacility = await resolveIsHubFacility();
    const newAssignments: AssignmentPayload[] = [];
    const reassignments: ReassignmentPayload[] = [];

    for (const studyInstanceUid of checkedStudyIds) {
      const study = selectedStudies.find(s => s.studyInstanceUid === studyInstanceUid);
      if (!study) {
        throw new Error(`Study ${studyInstanceUid} not found or missing modality information`);
      }
      const classified = classifyStudyForAssignment({
        study,
        studyInstanceUid,
        selectedRadiologist: radiologistId,
        isJuniorRadiologist,
        isSeniorRadiologist,
        hubFacility,
      });
      if (classified.kind === 'reassignment') {
        reassignments.push(classified.payload);
      } else {
        newAssignments.push(classified.payload);
      }
    }

    return { newAssignments, reassignments };
  };

  const executeAssignBatches = async (
    radiologistId: number,
    newAssignments: AssignmentPayload[],
    reassignments: ReassignmentPayload[]
  ) => {
    const batchAssignmentResults =
      newAssignments.length === 0
        ? []
        : await batchAssignStudies({
            radiologist_id: radiologistId,
            assignment_status: 'assigned',
            studies: newAssignments,
          });

    const reassignmentResults =
      reassignments.length === 0
        ? []
        : await batchReassignStudies({
            radiologist_id: reassignments[0].radiologist_id,
            assignment_status: reassignments[0].assignment_status,
            studies: convertReassignmentsToBatchFormat(reassignments, selectedStudies),
          });

    return { batchAssignmentResults, reassignmentResults };
  };

  const finishAssignSuccess = (
    radiologistId: number,
    newlyAssignedCount: number,
    reassignedCount: number,
    successCount: number
  ) => {
    const successMessage = buildAssignSuccessMessage(
      t,
      newlyAssignedCount,
      reassignedCount,
      successCount,
      selectedRadiologistName
    );
    if (uiNotificationService) {
      uiNotificationService.show({
        title: t('Success'),
        message: successMessage,
        type: 'success',
        duration: 5000,
      });
    }
    if (reassignedCount > 0 && onReassign) {
      onReassign(radiologistId, selectedRadiologistName);
    } else {
      onAssign(radiologistId, selectedRadiologistName);
    }
    onSuccess?.();
    onClose();
  };

  /**
   * Assign or reassign studies to radiologist
   */
  const handleAssign = async () => {
    const precheckError = getAssignPrecheckError();
    if (precheckError) {
      const duration = selectedRadiologist === null ? 3000 : 5000;
      showAssignValidationError(precheckError, duration);
      return;
    }

    if (selectedRadiologist == null) {
      return;
    }
    const radiologistId = selectedRadiologist;
    setIsAssigning(true);

    try {
      const { newAssignments, reassignments } = await partitionStudiesForAssign(radiologistId);
      const { batchAssignmentResults, reassignmentResults } = await executeAssignBatches(
        radiologistId,
        newAssignments,
        reassignments
      );
      finishAssignSuccess(
        radiologistId,
        batchAssignmentResults.length,
        reassignmentResults.length,
        batchAssignmentResults.length + reassignmentResults.length
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('Failed to assign studies');
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Assignment Failed'),
          message: errorMessage,
          type: 'error',
          duration: 5000,
        });
      }
    } finally {
      setIsAssigning(false);
    }
  };

  /**
   * Handle unassign studies
   */
  const handleUnassign = async () => {
    const showValidationError = (message: string) => {
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Validation Error'),
          message,
          type: 'error',
          duration: 5000,
          allowDuplicates: true,
        });
      }
    };

    const validation = validateStudySelection(selectedStudies);
    if (!validation.isValid) {
      showValidationError(t(validation.errorMessage || 'Invalid study selection'));
      return;
    }

    const studiesWithoutId = selectedStudies.filter(
      study => !study.studyId || study.studyId === null || study.studyId === undefined
    );
    if (studiesWithoutId.length > 0) {
      showValidationError(t('Some studies do not have study_id and cannot be unassigned'));
      return;
    }

    setIsAssigning(true);

    try {
      const { API_ENDPOINTS } = await import('../../config/api.config');
      const headers = getAuthHeaders();
      const studiesForUnassign = selectedStudies
        .filter(study => checkedStudyIds.includes(study.studyInstanceUid))
        .map(study => ({
          study_id: Number(study.studyId),
        }));

      const response = await fetch(API_ENDPOINTS.STUDIES.REASSIGN, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          status: 'UNASSIGNED',
          studies: studiesForUnassign,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unassign studies: ${response.statusText}`);
      }

      const responseData = await response.json();
      if (!responseData.success) {
        throw new Error(responseData.message || 'Failed to unassign studies');
      }

      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Success'),
          message: t('Successfully unassigned {{count}} study(ies)', {
            count: studiesForUnassign.length,
          }),
          type: 'success',
          duration: 5000,
        });
      }

      if (onUnassign) {
        onUnassign();
      }
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('Failed to unassign studies');
      if (uiNotificationService) {
        uiNotificationService.show({
          title: t('Unassignment Failed'),
          message: errorMessage,
          type: 'error',
          duration: 5000,
        });
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const studyCount = studiesForDisplay.length || checkedStudyIds.length;

  const priorityBadgeLabel = (study: SelectedStudy) => {
    if (study.isCritical) {
      return t('Critical');
    }
    const formatted = formatPriorityDisplay(study.priority);
    if (formatted) {
      return formatted;
    }
    return t('Routine');
  };

  const priorityBadgeUrgent = (study: SelectedStudy) =>
    Boolean(study.isCritical || priorityUsesUrgentPill(study.priority));

  return (
    <div className="-mt-2 font-sans text-[#000000]">
      <div className="border-b border-[#E0E0E0] px-6 pb-4 pr-14 pt-6">
        <h2 className="text-[22px] font-semibold leading-snug tracking-tight text-[#000000]">
          {hasAssignedStudies
            ? t('Reassign Studies to Radiologist')
            : t('Assign Studies to Radiologist')}
        </h2>
        <p className="mt-1.5 text-sm leading-snug text-[#717171]">
          {hasAssignedStudies
            ? t('Select a radiologist to reassign the selected studies for reporting')
            : t('Select a radiologist to assign the selected studies for reporting')}
        </p>
      </div>

      <div className="flex flex-col gap-6 px-6 py-6">
        <section>
          <h3 className="mb-3 text-sm font-semibold text-[#000000]">
            {t('Selected Studies')} ({studyCount})
          </h3>
          <div className="flex max-h-52 flex-col gap-2 overflow-y-auto pr-0.5">
            {studiesForDisplay.map(study => {
              const pid = study.patientId?.trim();
              const metaLine = pid ? `UHID-${pid}` : '';
              const urgent = priorityBadgeUrgent(study);
              const showCurrentAssignee =
                hasAssignedStudies && isStudyAssigned(study) && study.assignedName?.trim();
              return (
                <div
                  key={study.studyInstanceUid}
                  className="rounded-lg border border-[#E0E0E0] bg-white px-4 py-3"
                >
                  <div className="text-sm font-medium text-[#333333]">
                    {study.patientName?.trim() || t('Unknown patient')}
                  </div>
                  <div className="mt-0.5 text-sm font-normal text-[#666666]">
                    {study.studyDescription?.trim() ||
                      study.modalities?.trim() ||
                      t('Study description unavailable')}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#666666]">
                    {metaLine ? <span className="min-w-0 break-words">{metaLine}</span> : null}
                    <span
                      className={
                        urgent
                          ? 'inline-flex shrink-0 rounded-full bg-[#FEE2E2] px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-[#B91C1C]'
                          : 'inline-flex shrink-0 rounded-full bg-[#F0F0F0] px-2.5 py-0.5 text-[11px] font-medium text-[#333333]'
                      }
                    >
                      {priorityBadgeLabel(study)}
                    </span>
                  </div>
                  {showCurrentAssignee ? (
                    <div className="mt-2 text-xs leading-snug text-[#666666]">
                      {t('Currently')}:{' '}
                      {stripSubRoleSuffixFromAssignedName(study.assignedName!.trim())}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-[#000000]">{t('Select Radiologist')}</label>
          <VirtualizedDropdown
            value={selectedRadiologist}
            onChange={handleRadiologistChange}
            options={radiologists}
            placeholder={t('Choose a radiologist...')}
            isLoading={isLoading}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder={t('Search radiologists...')}
            emptyMessage={t('No radiologists found for selected modality')}
            loadingMessage={t('Loading radiologists...')}
            className="w-full"
            showNone={false}
            variant="light"
            truncateTriggerLabel={false}
            richSubtitlePrimaryClassName="w-full text-sm font-medium leading-tight text-[#333333]"
            richSubtitleSecondaryClassName="w-full text-xs font-normal leading-snug text-[#9ca3af]"
          />
          <p className="text-xs leading-relaxed text-[#717171]">
            {t("Studies will appear in the selected radiologist's worklist for reporting")}
          </p>
        </section>
      </div>

      <div className="flex flex-wrap justify-end gap-3 border-t border-[#E0E0E0] px-6 py-4">
        <Button
          variant="outline"
          onClick={onClose}
          className="min-w-[96px] rounded-md border-[#E0E0E0] bg-white px-4 py-2 text-[#000000] hover:bg-[#FAFAFA]"
          disabled={isAssigning}
        >
          {t('Cancel')}
        </Button>
        {hasAssignedStudies && onUnassign && (
          <Button
            variant="outline"
            onClick={handleUnassign}
            disabled={isLoading || isAssigning}
            className="min-w-[96px] rounded-md px-4 py-2"
            style={{ color: '#ef4444', borderColor: '#ef4444' }}
          >
            {isAssigning ? t('Unassigning...') : t('Unassign')}
          </Button>
        )}
        <Button
          variant="default"
          onClick={handleAssign}
          disabled={selectedRadiologist === null || isLoading || isAssigning}
          className="min-w-[120px] rounded-md border-0 px-4 py-2 text-white hover:brightness-95 disabled:opacity-50 disabled:hover:brightness-100"
          style={{ backgroundColor: '#118ab2' }}
        >
          {isAssigning
            ? hasAssignedStudies
              ? t('Reassigning...')
              : t('Assigning...')
            : hasAssignedStudies
              ? t('Reassign to Radiologist')
              : t('Assign to Radiologist')}
        </Button>
      </div>
    </div>
  );
};

export default AssignRadiologistModal;
